"use client";

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { CreateSourcedInviteRequest, TalentFeedQuery } from "@chotca/sdk";
import { getSdk } from "../sdk";
import { qk } from "../query-keys";

const PAGE_LIMIT = 20;

/** Infinite "Tìm ứng viên" deck for an org. `filters` excludes cursor/limit. */
export function useTalentFeed(
  orgId: string,
  filters: Omit<TalentFeedQuery, "cursor" | "limit"> = {},
  enabled = true,
) {
  return useInfiniteQuery({
    queryKey: qk.talent.feed(orgId, filters as Record<string, unknown>),
    enabled: enabled && !!orgId,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) =>
      getSdk().talent.feed(orgId, {
        ...filters,
        limit: PAGE_LIMIT,
        cursor: pageParam,
      }),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    staleTime: 15_000,
  });
}

/** Full CV detail for one candidate — opened when the employer taps a card. */
export function useTalentProfile(
  orgId: string,
  workerId: string | null,
  enabled = true,
) {
  return useQuery({
    queryKey: qk.talent.profile(orgId, workerId ?? ""),
    queryFn: () => getSdk().talent.profile(orgId, workerId!),
    enabled: enabled && !!orgId && !!workerId,
  });
}

/** Source a worker into a posting (spends 1 lượt, sends a sourced invite). */
export function useCreateSourcedInvite(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      workerId,
      body,
    }: {
      workerId: string;
      body: CreateSourcedInviteRequest;
    }) => getSdk().talent.invite(orgId, workerId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.talent.feed(orgId) });
      qc.invalidateQueries({ queryKey: qk.orgs.detail(orgId) });
      // Sourcing spends a use → refresh plan status so the count updates now.
      qc.invalidateQueries({ queryKey: qk.orgs.planStatus(orgId) });
      qc.invalidateQueries({ queryKey: qk.applications.creditLedger });
      qc.invalidateQueries({ queryKey: qk.invites.byOrg(orgId) });
    },
  });
}
